<template>
  <div>
    <span class="primary--text" v-if="!retrying">
      Only upload the two Scan Gauge Scans of a single arch. One left to right
      and one right to left Scan.
    </span>
    <span class="primary--text" v-if="retrying">
      Attempt {{ retryCount + 1 }} - Upload at least one new scan.
    </span>

    <div v-if="retrying && faceCount" class="py-2">
      Expected Gauge Selection Count: <strong> {{ faceCount }} </strong>
      <v-btn small @click="restartSession" class="ml-3 elevation-0 rounded-10">
        Restart
      </v-btn>
    </div>

    <v-data-table
      v-if="retrying"
      :headers="[
        { text: 'Attempt', value: 'attempt' },
        { text: 'Scan 1', value: 'scan1' },
        { text: 'Scan 2', value: 'scan2' }
      ]"
      :items="uploadedScans"
      disable-sort
      hide-default-footer
    />
    <v-card flat>
      <v-card-text class="px-1 pt-3">
        <v-form ref="form">
          <v-row>
            <v-col cols="12" class="px-2">
              <ScanUploader
                categoryTitle="Scan Gauge Scans"
                v-model="files"
                :disabled="files.length === 2"
              />
            </v-col>
            <v-col cols="12" class="pt-0 ms-2">
              <v-switch
                v-model="_narrowScanBodiesPresent"
                label="Include Narrow Scan Bodies"
                inset
                dense
                class="mt-0 pt-0 title-font font-weight-medium"
                :disabled="loading"
              />
            </v-col>
          </v-row>
          <div class="d-flex">
            <v-btn
              class="btn-primary"
              @click="handleContinue"
              :disabled="
                loading ||
                files.length !== 2 ||
                (!scansChanged && uploadedScans.length === retryCount)
              "
            >
              <span> Continue </span>
            </v-btn>
            <v-btn
              v-if="
                scanUploadError ||
                (uploadedScans.length &&
                  retrying &&
                  !scansChanged &&
                  uploadedScans.length === retryCount)
              "
              class="elevation-0 rounded-10 ml-3"
              @click="reupload"
            >
              Re-upload Scans
            </v-btn>
            <v-scale-transition origin="center center">
              <div class="flex-grow-1 ml-3 mr-1" v-if="loading">
                <v-progress-linear
                  rounded
                  indeterminate
                  color="primary"
                  height="36"
                  class="rounded-10"
                  background-opacity="0.6"
                >
                  <span
                    class="text-body-2 font-weight-medium white--text loading-pulse"
                  >
                    PROCESSING...
                  </span>
                </v-progress-linear>
              </div>
            </v-scale-transition>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
    <span class="warning--text" v-if="!retrying">
      <strong>Please note: </strong>The Nexus Analyzer cannot detect if gauges
      are seated or touching. Confirm arrangement, MUA seating and gaps before
      scanning.
    </span>
    <v-dialog
      v-model="uploading"
      max-width="600"
      persistent
      content-class="rounded-10"
    >
      <v-card class="card-bg rounded-10" v-if="uploading">
        <v-card-text class="text-center pt-5">
          <v-row align="center" justify="center">
            <v-col cols="12" class="pa-5">
              <v-progress-circular
                :rotate="-90"
                :width="8"
                :size="200"
                :value="uploadProgress"
                color="primary"
              >
                <span class="loading-pulse">
                  Uploading Files...
                  <br />
                  {{ uploadProgress }}%
                </span>
              </v-progress-circular>
            </v-col>
          </v-row>
          <p :class="['mt-5']">
            Please wait for your uploads to complete before continuing.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { getTextColour } from "@/utils"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import ScanUploader from "./UploadScans/ScanUploader.vue"
import oftClient from "@/lib/OftApi"

export default {
  name: "UploadScans",

  components: { ScanUploader },

  data() {
    return {
      getTextColour,
      loading: false,
      uploadProgress: 0,
      failedUploads: [],
      currentUpload: 0,
      uploading: false,
      fileRules: [
        (files) => {
          if (files) {
            files.forEach((file) => {
              const { name } = file
              const ext = name.split(".").pop().toUpperCase()
              if (ext === "STL" || ext === "PLY") {
                return true
              }
              return "File must be an STL or PLY."
            })
          } else {
            return "You must select two files."
          }
        }
      ]
    }
  },

  computed: {
    ...mapGetters([
      "ncSessionID",
      "scanFiles",
      "scanUploadError",
      "orderDetails",
      "orderUid",
      "orderSessionUid",
      "uploadedScans",
      "narrowScanBodiesPresent",
      "addScansResponse",
      "faceCount",
      "retryCount",
      "retrying"
    ]),

    _narrowScanBodiesPresent: {
      get() {
        return this.narrowScanBodiesPresent
      },
      set(value) {
        this.setNarrowScanBodiesPresent(value)
      }
    },

    scansChanged() {
      if (!this.retrying) return true
      if (!this.files.length) return true
      return !this.files.every((file) => file.uploaded)
    },

    bypass() {
      return (
        (!this.retrying && this.uploadedScans.length) ||
        this.uploadedScans.length === this.retryCount + 1
      )
    },

    files: {
      get() {
        return this.scanFiles
      },
      set(value) {
        return this.setScanFiles(value)
      }
    }
  },

  methods: {
    ...mapActions([
      "setNCSessionID",
      "setScanFiles",
      "setOrderDetails",
      "setScanUploadError",
      "setUploadedScans",
      "setNarrowScanBodiesPresent",
      "setAddScansResponse",
      "setTransition",
      "setErrorMessage",
      "restartSession"
    ]),

    async handleContinue() {
      if (!this.$refs.form.validate()) return
      if (this.bypass) return this.$emit("next")

      try {
        this.loading = true

        if (!this.retrying) {
          const response = await NCFrontend.ApiClient.sessionCreate(
            "/proxy",
            ""
          )
          this.setNCSessionID(response.sessionId)
        }

        await this.uploadScans()

        this.setAddScansResponse(
          await NCFrontend.ApiClient.sessionAddScans("/proxy", "", {
            sessionId: this.ncSessionID,
            scan1FileUrl: this.files[0].url,
            scan1FileOftUid: this.files[0].oftUid,
            scan2FileUrl: this.files[1].url,
            scan2FileOftUid: this.files[1].oftUid,
            narrowScanBodiesPresent: this.narrowScanBodiesPresent,
            retrying: this.retrying
          })
        )

        this.setUploadedScans([
          ...this.uploadedScans,
          {
            attempt: this.uploadedScans.length + 1,
            scan1: this.files[0].file.name,
            scan2: this.files[1].file.name
          }
        ])

        this.setScanUploadError(false)
        this.setTransition(true)
        this.$emit("next")
      } catch (err) {
        this.setScanUploadError(true)
        if (err instanceof NCFrontend.ApiClient.ErrorResponse) {
          switch (err.type) {
            case NCFrontend.ApiClient.ErrorType.IdenticalScanFiles:
              this.setErrorMessage("Identical scans detected.")
              break
            case NCFrontend.ApiClient.ErrorType.ResourceNotFound:
              this.setErrorMessage("Failed to find session.")
              break
            case NCFrontend.ApiClient.ErrorType.MaxValidationRetriesReached:
              this.setErrorMessage("Max retry attempts reached.")
              break
            case NCFrontend.ApiClient.ErrorType.ScansAlreadyUsedInValidation:
              this.setErrorMessage(
                "Scans have already been used in an attempt."
              )
              break
            case NCFrontend.ApiClient.ErrorType.ServerError:
              this.setErrorMessage("An internal server error occurred.")
              break
          }
        } else this.setErrorMessage("An internal error occurred.")
      } finally {
        this.loading = false
      }
    },

    reupload() {
      this.files.forEach((file) => file.revokeObjectUrl())
      this.files = []
    },

    async uploadScans() {
      this.uploading = true

      try {
        await oftClient.uploadFiles(this.files, (progressValue) => {
          this.uploadProgress = progressValue
        })
        await oftClient.getFileUrls(this.files)
      } catch (err) {
        console.log(err)
        this.setErrorMessage("Failed to upload scans")
      } finally {
        this.uploading = false
        this.uploadProgress = 0
      }
    }
  }
}
</script>
